// React component for the connect modal
import React, { useState } from 'react';
import { FuelCardConfig, FuelCardProvider } from '../../../../redux/api/fuelcard/fuelcard.model';
import TwDialog from '../../../../components/TwDialog/TwDialog';
import {
  TwButton,
  TwButtonType,
  TwLoadingButton
} from '../../../../components/TwButtons/TwButtons';
import TwField, { TwInputTypes } from '../../../../components/TwFields/TwFields';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { useAddFuelCardConfigMutation } from '../../../../redux/api/fuelcard/fuelcardApi';
import useAuth from '../../../../providers/Auth/Auth.provider';

interface ConnectModalProps {
  open: boolean;
  provider: FuelCardProvider;
  onClose: () => void;
  onConnect?: (config: FuelCardConfig) => void;
  divisionId: string;
}

export const getProviderLabel = (provider: FuelCardProvider): string => {
  switch (provider) {
    case FuelCardProvider.EFS:
      return 'Wex | EFS | FleetOne';
    default:
      return provider;
  }
};

const ConnectModal: React.FC<ConnectModalProps> = ({
  open,
  onClose,
  provider,
  divisionId,
  onConnect
}) => {
  const {
    data: { userData }
  } = useAuth();

  const { addNotification } = useNotifications();
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errors, setErrors] = useState<{ username: boolean; password: boolean }>({
    username: false,
    password: false
  });

  const [addFuelCardConfig, { isLoading }] = useAddFuelCardConfigMutation();

  const handleSubmit = async (): Promise<void> => {
    // validation
    if (!userName) {
      setErrors({ ...errors, username: true });
      addNotification({
        message: 'Username is required',
        variant: 'error',
        timeToShow: 2000
      });
      return;
    }

    if (!password) {
      setErrors({ ...errors, password: true });
      addNotification({
        message: 'Password is required',
        variant: 'error',
        timeToShow: 2000
      });
      return;
    }

    addFuelCardConfig({
      divisionId,
      fuelcardProvider: provider,
      username: userName,
      password: password,
      tenantId: userData?.tenant?.id || ''
    })
      .unwrap()
      .then((res) => {
        addNotification({
          message: 'Account connected successfully',
          variant: 'success',
          timeToShow: 3000
        });
        onClose();
      })
      .catch((err) => {
        console.error('err', err);
        if (err.status === 400) {
          addNotification({
            message: 'Username or password is incorrect',
            variant: 'error',
            timeToShow: 3000
          });
        } else {
          addNotification({
            message: 'Unable to connect',
            variant: 'error',
            timeToShow: 3000
          });
        }
      });
  };

  return (
    <TwDialog
      open={open}
      title={`Connect to ${getProviderLabel(provider)}`}
      size="sm"
      onClickClose={onClose}
      actions={[
        <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={onClose} />,
        <TwLoadingButton
          key="2"
          type={TwButtonType.primary}
          label="Connect"
          disabled={false}
          action={handleSubmit}
          loading={isLoading}
        />
      ]}
    >
      <TwField
        id="username"
        name="username"
        value={userName}
        label="Username"
        inputType={TwInputTypes.text}
        disabled={false}
        onInputChange={(e): any => setUserName(e.target.value)}
        error={!!errors.username}
      />

      <TwField
        id="password"
        name="password"
        value={password}
        label="Password"
        inputType={TwInputTypes.password}
        disabled={false}
        onInputChange={(e): any => setPassword(e.target.value)}
        error={!!errors.password}
      />
    </TwDialog>
  );
};

export default ConnectModal;
